import type { RumEvent } from "@datadog/browser-rum";

/**
 * RUM `beforeSend` noise filter: returns true for error events that are not
 * ours to fix (third-party scripts, benign browser behaviour) so they never
 * reach Error Tracking. Pure and unit-tested in rumNoise.test.ts.
 */
export function isNoiseRumError(event: RumEvent): boolean {
  if (event.type !== "error") return false;
  const message = event.error?.message ?? "";
  const stack = event.error?.stack ?? "";
  // Only set for console / addError / logger errors; undefined for
  // window.onerror and unhandled rejections, whose frames live in `stack`.
  const handlingStack = event.error?.handling_stack ?? "";
  const frames = `${stack}\n${handlingStack}`;

  // Outlook SafeLink / O365 link-scanner injection — not our code.
  if (/Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4/.test(message)) {
    return true;
  }
  // AbortController cancellations on unmount/route change — benign.
  if (message === "signal is aborted without reason") return true;
  // CORS-blinded 3rd-party script errors (gtag, Vimeo, etc.) — empty stack, non-actionable.
  if (message.includes("Script error.") && !stack.includes("fivestar.video")) return true;
  // Third-party / automation frames anywhere in the call chain, but only when
  // none of our own bundle frames are present: a vendor callback or a
  // Playwright page.evaluate can synchronously invoke our handlers, and an
  // error raised there is still ours.
  if (!hasFirstPartyFrame(frames)) {
    // ZoomInfo WebSights tag logs its own fetch failures via console.error
    // ("ZI error SyntaxError: Unexpected token '<' ..." when its entitlements
    // endpoint answers with HTML — seen only from crawler traffic). The SDK
    // captures console errors as handled errors; the vendor script is the only
    // frame, so nothing here is actionable by us.
    if (message.startsWith("ZI error") || frames.includes("zi-scripts.com")) return true;
    // Playwright-driven scrapers: page.evaluate runs inside an injected
    // "UtilityScript" and their own console.error calls land in our RUM app
    // (e.g. a bot probing every iframe and hitting cross-origin SecurityErrors).
    if (frames.includes("UtilityScript")) return true;
  }
  // Spec-mandated ResizeObserver notice, not an exception: fires via
  // window.onerror when an observer callback changed layout in the same frame.
  // Delivery resumes next frame, nothing is lost. There is no JS frame to scope
  // on, so this drop is global; as of Sep 2026 every occurrence came from
  // headless / crawler sessions. A real layout-thrash regression in one of our
  // observers would surface as Long Task / INP degradation, not here.
  if (/ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(message)) {
    return true;
  }

  return false;
}

/** Our Vite bundles are always served from `<origin>/assets/<name>-<hash>.js`,
 *  on production and on the Amplify staging host alike. */
function hasFirstPartyFrame(frames: string): boolean {
  return /\/assets\/[^\s/]+\.js/.test(frames);
}
